<script setup lang="ts">
/**
 * /alternatives — the hub for the competitor comparisons. One line of fact per
 * builder (how it bills), then the page for each.
 */
import { COMPETITORS } from '~/constants/competitors'
import { localizeCompetitor } from '~/constants/competitorsCs'
import { JOB_PACK_DAYS } from '~/constants/pricing'

const { t, locale } = useI18n()
const competitors = computed(() => COMPETITORS.map(competitor => localizeCompetitor(competitor, locale.value)))

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/alternativy', de: '/alternativen', pl: '/alternatywy' } })

const { prices, format } = usePricing().pricing.value
const priceParams = {
  export: format(prices.exportCredit),
  pack: format(prices.jobPack),
  days: JOB_PACK_DAYS,
}
</script>

<template>
  <MarketingFeaturePage
    seo-key="alternatives"
    path="/alternatives"
    :breadcrumb-title="t('alternatives.hub.heading')"
  >
    <div class="sofax">
      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-sm">
          <div class="shell">
            <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                {{ t('alternatives.page.shortLabel') }}
              </div>
              <p class="text-lg">{{ t('alternatives.hub.short', priceParams) }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-bottom">
          <div class="shell">
            <SofaxSectionHeading
              :eyebrow="t('alternatives.hub.list.eyebrow')"
              :title="t('alternatives.hub.list.title')"
              width="max-w-4xl"
            />
            <ul class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
              <li
                v-for="competitor in competitors"
                :key="competitor.slug"
                class="flex flex-col rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
              >
                <h3 class="!text-2xl">{{ competitor.name }}</h3>
                <p class="mt-3 flex-1 text-base">{{ competitor.summaryLine }}</p>
                <div class="mt-5">
                  <SofaxArrowLink :to="`/alternatives/${competitor.slug}`" :label="t('alternatives.hub.list.cta', { name: competitor.name })" />
                </div>
              </li>
            </ul>
            <p class="mx-auto mt-8 max-w-5xl text-sm text-muted dark:text-gray-400">{{ t('alternatives.hub.list.note') }}</p>
          </div>
        </div>
      </section>
    </div>
  </MarketingFeaturePage>
</template>
